import type { AnchorHTMLAttributes, ReactNode } from 'react'
import { getOnlyNumber } from 'utils/convertor'

interface PhoneLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** The phone number to display and dial. May be pre-formatted, e.g. "(888) 247-4830" or "+44 20 7946". */
  value?: string | number | null
  /** Optional custom display content; defaults to the raw `value`. */
  children?: ReactNode
  /** Calling code used when the number carries no explicit country code. Defaults to US/Canada. */
  defaultCountryCode?: string
  /** Rendered when there is no dialable number. Defaults to the raw value as plain text. */
  fallback?: ReactNode
}

/**
 * Builds a `tel:` href from a loosely-formatted phone string.
 * Returns null when there is nothing dialable so callers can fall back to plain text.
 */
export function toTelHref(value?: string | number | null, defaultCountryCode = '1'): string | null {
  if (value == null) return null
  const raw = String(value).trim()
  const digits = getOnlyNumber(raw)
  if (!digits) return null
  // Explicit international prefix — preserve the country code the user typed.
  if (raw.startsWith('+')) return `tel:+${digits}`
  // North American number already carrying the leading country code.
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`
  // Bare 10-digit North American number.
  if (digits.length === 10) return `tel:+${defaultCountryCode}${digits}`
  // Unknown shape (extension-only, short code, intl without +): dial the digits as-is.
  return `tel:${digits}`
}

/**
 * Renders a phone number as a click-to-call `tel:` anchor. Falls back to plain text when the
 * value has no dialable digits, and stops click propagation so it is safe inside clickable
 * table rows and cards. Styling defaults to inheriting the surrounding text with a hover
 * underline; pass `className` to override.
 */
export function PhoneLink({
  value,
  children,
  defaultCountryCode = '1',
  fallback,
  className = '',
  onClick,
  ...rest
}: PhoneLinkProps) {
  const href = toTelHref(value, defaultCountryCode)
  const display = children ?? (value == null ? '' : String(value))

  if (!href) return <>{fallback ?? display}</>

  return (
    <a
      href={href}
      className={`text-inherit hover:underline ${className}`.trim()}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(e)
      }}
      {...rest}
    >
      {display}
    </a>
  )
}
